import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  EditOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  App,
  Avatar,
  Button,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Skeleton,
  TimePicker,
  Typography,
} from 'antd';
import {
  completeTask,
  createTask,
  fetchTasks,
  updateTask,
} from 'api/tasks';
import ContactSelectInput from 'components/ContactSelectInput';
import UserSelectInput from 'components/UserSelectInput';
import { AdminContext } from 'constants/contexts';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { captureException } from 'utils/errors';

const { Text } = Typography;
const completedTasks = [];
function Tasks({ companyId }) {
  const [tasks, setTasks] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  const { user } = useContext(AdminContext);


  // Load tasks from API on component mount
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchTasks(companyId);
      // Filter out completed tasks as per requirement
      const activeTasks = response.results ? response.results.filter((task) => !task.is_completed || completedTasks.includes(task.id)) : [];
      setTasks(activeTasks);
    } catch (error) {
      captureException(error);
      message.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [companyId, message]);

  useEffect(() => {
    if (companyId) {
      loadTasks();
    }
  }, [companyId, loadTasks]);

  // Handle form submission (create or edit)
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const taskData = {
        // title: values.title,
        title: 'Task',
        description: values.description,
        assignee: values.assignee?.value || values.assignee,
        // todo: it's for test
        // assignee: 1,
        due_date: values.date && values.time
          ? dayjs(values.date)
            .hour(dayjs(values.time).hour())
            .minute(dayjs(values.time).minute())
            .second(0)
            .toISOString()
          : null,
        contact: values.contact?.value || null,
      };

      if (editingTask) {
        // Update existing task
        await updateTask(companyId, editingTask.id, taskData);
        message.success('Task updated successfully');
      } else {
        // Create new task
        await createTask(companyId, taskData);
        message.success('Task created successfully');
      }

      // Reload tasks and reset form
      await loadTasks();
      form.resetFields();
      setShowCreateForm(false);
      setEditingTask(null);
    } catch (error) {
      captureException(error);
      message.error('Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  // Handle task completion
  const handleCompleteTask = async (taskId) => {
    try {
      await completeTask(companyId, taskId);
      completedTasks.push(taskId);

      message.success('Task marked as complete');
      // Reload tasks to refresh the list
      await loadTasks();
    } catch (error) {
      captureException(error);
      message.error('Failed to complete task');
    }
  };

  // Handle task editing
  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowCreateForm(true);

    // Populate form with task data
    form.setFieldsValue({
      title: task.title,
      description: task.description,
      assignee: task.assignee ? {
        value: task.assignee.id,
        label: task.assignee.first_name && task.assignee.last_name ? `${task.assignee.first_name} ${task.assignee.last_name}` : task.assignee.email,
      } : null,
      date: task.due_date ? dayjs(task.due_date) : null,
      time: task.due_date ? dayjs(task.due_date) : null,
      contact: task.contact ? {
        value: task.contact.id,
        label: task.contact.email || `${task.contact.first_name || ''} ${task.contact.last_name || ''}`.trim(),
      } : null,
    });
  };

  // Cancel form
  const handleCancel = () => {
    form.resetFields();
    setShowCreateForm(false);
    setEditingTask(null);
  };

  // Filter tasks - tasks are already filtered in loadTasks to only show active ones
  const activeTasks = tasks;

  return (
    <div className="border border-gray-200 rounded-lg mt-4 px-3 py-2">
      {/* Tasks Section Title */}
      <div className="flex justify-between items-center mb-0">
        <Typography.Title level={4} className="mb-0 font-bold">
          Tasks
        </Typography.Title>
        {!showCreateForm && (
          <Button
            icon={<PlusOutlined />}
            onClick={() => setShowCreateForm(true)}
            className="text-gray-600 hover:text-gray-900"
          >
            {tasks.length === 0 ? 'Add' : ''}
          </Button>
        )}
      </div>

      {/* Create/Edit Task Form */}
      {showCreateForm && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-4"
          initialValues={{
            assignee: {
              value: user.id,
              label: user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email,
            },
          }}
        >
          <Form.Item
            name="description"
            rules={[{ required: true, message: 'Please enter task description' }]}
          >
            <Input
              placeholder="Task description"
              maxLength={500}
              allowClear
              size="large"
            />
          </Form.Item>

          {/* <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              placeholder="Enter task description..."
              maxLength={1000}
              allowClear
              size="large"
              rows={3}
            />
          </Form.Item> */}

          <Form.Item
            name="assignee"
            label="Assignee"
            rules={[{ required: true, message: 'Please select assignee' }]}
          >
            <UserSelectInput
              placeholder="Select assignee"
              suffixIcon={<UserOutlined />}
              size="large"
              renderLabel={(user) => (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email)}
            />
          </Form.Item>

          <Typography.Text>Reminder Date & Time</Typography.Text>
          <Flex gap={8} className="mt-2 w-full">
            <Form.Item
              name="date"
              // label="Reminder Date"
              style={{ width: '50%' }}
              rules={[{ required: true, message: 'Please select date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Select date"
                suffixIcon={<CalendarOutlined />}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
                size="large"

              />
            </Form.Item>
            <Form.Item
              name="time"
              // label="Reminder Time"
              style={{ width: '50%' }}
              rules={[{ required: true, message: 'Please select time' }]}
            >
              <TimePicker
                style={{ width: '100%' }}
                placeholder="Select time"
                format="HH:mm"
                suffixIcon={<ClockCircleOutlined />}
                size="large"
                minuteStep={15}
              />
            </Form.Item>
          </Flex>

          <Form.Item
            name="contact"
            label={(
              <>
                <Typography.Text>Contact</Typography.Text>

                <Typography.Text type="secondary" className="ml-2">optional</Typography.Text>
              </>
            )}
          >
            <ContactSelectInput
              placeholder="Select contact"
              companyId={companyId}
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Flex justify="end" gap={8}>
              <Button onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingTask ? 'Save' : 'Create'}
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      )}

      {/* Tasks List - Filled State */}
      {activeTasks.length > 0 && !showCreateForm && (
        <div className="mt-2">
          {activeTasks.map((task, index) => (
            <div key={task.id}>
              <div
                className="py-4 rounded-lg transition-all duration-200 hover:border-gray-300"
                style={{
                  opacity: task.is_completed ? 0.5 : 1,
                }}
              >
                <div className="space-y-3">
                  {/* Task Title */}
                  <div className="flex justify-between items-start gap-3">
                    <Text className={`text-base font-medium text-gray-900 m-0 ${task.is_completed ? 'line-through opacity-60' : ''}`}>
                      {task.description}
                    </Text>
                    {
                      !task.is_completed && (
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditTask(task)}
                            className="w-8 h-8 flex items-center justify-center rounded hover:bg-blue-50 hover:text-blue-700"
                            size="small"
                          />
                          <Button
                            type="text"
                            icon={<CheckOutlined />}
                            onClick={() => handleCompleteTask(task.id)}
                            className="w-8 h-8 flex items-center justify-center rounded hover:bg-green-50 disabled:text-gray-300 disabled:bg-transparent"
                            disabled={task.is_completed}
                            size="small"
                          />
                        </div>
                      )
                    }

                  </div>

                  {/* Assignee */}
                  {task.assignee && (
                    <div className="flex items-center gap-2">
                      <Avatar
                        size={18}
                        style={{
                          backgroundColor: '#FFE4D6',
                          color: '#D2691E',
                          fontWeight: 600,
                          fontSize: '11px',
                        }}
                        // className="bg-orange-400 text-white text-xs font-medium"
                      >
                        {(() => {
                          const name = task.assignee.first_name && task.assignee.last_name
                            ? `${task.assignee.first_name} ${task.assignee.last_name}`
                            : task.assignee.email || task.assignee.username;
                          return name.split(' ').map((n) => n[0]).join('').toUpperCase()
                            .slice(0, 2);
                        })()}
                      </Avatar>
                      <Text className="text-sm text-gray-600 m-0">
                        {task.assignee.first_name && task.assignee.last_name
                          ? `${task.assignee.first_name} ${task.assignee.last_name}`
                          : task.assignee.email || task.assignee.username}
                      </Text>
                    </div>
                  )}

                  {/* Due Date */}
                  {task.due_date && (
                    <div className="flex items-center gap-2">
                      <CalendarOutlined className="text-gray-400 text-sm w-4 h-4" />
                      <Text className="text-sm text-gray-600 m-0">
                        {dayjs(task.due_date).format('MMM D, YYYY, h:mm A')}
                      </Text>
                    </div>
                  )}

                  {/* Contact */}
                  {task.contact && (
                    <div className="flex items-center gap-2">
                      <UserOutlined className="text-gray-400 text-sm w-4 h-4" />
                      <Text className="text-sm text-gray-600 m-0">
                        {task.contact.email || `${task.contact.first_name || ''} ${task.contact.last_name || ''}`.trim()}
                      </Text>
                    </div>
                  )}
                </div>
              </div>

              {/* Divider between tasks (not after the last one) */}
              {index < activeTasks.length - 1 && (
                <Divider className="my-0" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Loading State */}
      {loading && !showCreateForm && !tasks?.length && (
        <Skeleton className="mt-2" paragraph={{ rows: 1 }} />
      )}
    </div>
  );
}

export default Tasks;
